import {
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import LearnerDashboardMobileNavigation from './navigation/LearnerDashboardMobileNavigation';
import LearnerDashboardSideNavigation from './navigation/LearnerDashboardSideNavigation';
import { Search2 } from '@/client/components/icons/ContinuIcons';
import WarmNeutralMenu from '../../shared/menus/WarmNeutralMenu';
import { useAuthStore } from '@/client/services/state/authStore';
import { useDebounceValue } from 'usehooks-ts';
import useDocumentTitle from '@/client/utils/useDocumentTitle';
import { useTranslation } from 'react-i18next';

// Custom date filter menu component
interface DateFilterMenuProps {
  label: string;
  selected: {
    name: string;
    value: string | null;
    after: string | null;
    before: string | null;
  } | null;
  setSelected: (option: {
    name: string;
    value: string | null;
    after: string | null;
    before: string | null;
  }) => void;
  options: {
    name: string;
    value: string | null;
    after: string | null;
    before: string | null;
  }[];
  minWidth?: number;
}

function DateFilterMenu({ label, selected, setSelected, options, minWidth }: DateFilterMenuProps) {
  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon color="baseBlack" />}
        backgroundColor="white"
        color="baseBlack"
        fontSize="14px"
        fontWeight={500}
        paddingY="14px"
        paddingX={3}
        border="1px solid"
        borderColor="warmNeutral.200"
        borderRadius="6px"
        minWidth={minWidth}
        textAlign="left"
        _hover={{ backgroundColor: 'warmNeutral.0' }}
        _active={{ backgroundColor: 'warmNeutral.0' }}
      >
        {selected?.name}
      </MenuButton>

      <MenuList
        border="1px solid"
        borderColor="warmNeutral.200"
        borderRadius="6px"
        boxShadow="0px 4px 12px 0px rgba(0, 0, 0, 0.15)"
        paddingX={4}
        maxHeight="200px"
        overflowY="auto"
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.value || `option-${index}`}
            color="baseBlack"
            fontSize="14px"
            fontWeight={500}
            borderBottom={index !== options.length - 1 ? '1px solid' : 'none'}
            borderBottomColor="neutral.100"
            paddingY={4}
            _hover={{ backgroundColor: 'warmNeutral.0' }}
            onClick={() => setSelected(option)}
          >
            {option.name}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

export default function LearnerDashboardLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const contentContainerRef = useRef<HTMLDivElement>(null);

  useDocumentTitle(t('learners.dashboard.overview.documentTitle'));

  const filterOptions = [
    { name: t('learners.dashboard.recentlyViewed.search.allContentTypes'), value: null },
    { name: t('learners.dashboard.recentlyViewed.search.file'), value: 'file' },
    { name: t('learners.dashboard.recentlyViewed.search.video'), value: 'video' },
    { name: t('learners.dashboard.recentlyViewed.search.article'), value: 'article' },
    { name: t('learners.dashboard.recentlyViewed.search.scorm'), value: 'scorm' },
    {
      name: t('learners.dashboard.recentlyViewed.search.importedContent'),
      value: 'imported_content',
    },
  ];

  const [selectedFilter, setSelectedFilter] = useState<{
    name: string;
    value: string | null;
  } | null>(filterOptions[0]);

  const [searchTerm, setSearchTerm] = useDebounceValue('', 300);

  // Generate date filter options from user.createdAt to current date
  const dateFilterOptions = useMemo(() => {
    if (!user?.createdAt) return [];

    const startDate = new Date(user.createdAt);
    const currentDate = new Date();
    const options = [];

    // Generate month/year options
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();

    for (let year = currentYear; year >= startYear; year--) {
      const monthStart = year === startYear ? startMonth : 0;
      const monthEnd = year === currentYear ? currentMonth : 11;

      for (let month = monthEnd; month >= monthStart; month--) {
        const date = new Date(year, month, 1);
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        options.push({
          name: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          value: `${year}-${String(month + 1).padStart(2, '0')}`,
          after: firstDayOfMonth.toISOString(),
          before: lastDayOfMonth.toISOString(),
        });
      }
    }

    return options;
  }, [user?.createdAt, t]);

  const [selectedDateFilter, setSelectedDateFilter] = useState<{
    name: string;
    value: string | null;
    after: string | null;
    before: string | null;
  } | null>(dateFilterOptions[0] || null);

  const isMobileOrTablet = useBreakpointValue({ base: true, md: true, lg: false });

  // Scroll to top when route changes
  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
  }, [pathname]);

  const routeMap: Record<
    string,
    {
      showHeader: boolean;
      pageTitle: string;
      search: { show: boolean; placeholder: string; useContentFilter: boolean };
    }
  > = {
    '/dashboard': {
      showHeader: false,
      pageTitle: t('learners.dashboard.overview.documentTitle'),
      search: {
        show: false,
        placeholder: '',
        useContentFilter: false,
      },
    },
    '/dashboard/assignments': {
      showHeader: true,
      pageTitle: t('learners.dashboard.assignments.yourAssignments'),
      search: {
        show: false, // TODO: Enable this when we have a search for assignments
        placeholder: t('learners.dashboard.assignments.search.placeholder'),
        useContentFilter: true,
      },
    },
    '/dashboard/assignments/assignment-details': {
      showHeader: false,
      pageTitle: t('learners.dashboard.assignments.assignmentDetails'),
      search: {
        show: false,
        placeholder: '',
        useContentFilter: false,
      },
    },
    '/dashboard/bookmarks': {
      showHeader: true,
      pageTitle: t('learners.dashboard.bookmarks.yourBookmarks'),
      search: {
        show: false, // TODO: Enable this when we have a search for bookmarks
        placeholder: t('learners.dashboard.bookmarks.search.placeholder'),
        useContentFilter: true,
      },
    },
    '/dashboard/recently-viewed': {
      showHeader: true,
      pageTitle: t('learners.dashboard.recentlyViewed'),
      search: {
        show: true,
        placeholder: t('learners.dashboard.recentlyViewed.search.placeholder'),
        useContentFilter: true,
      },
    },
    '/dashboard/recommendations': {
      showHeader: true,
      pageTitle: t('learners.dashboard.recommendations.title'),
      search: {
        show: false, // TODO: Enable this when we have a search for recommendations
        placeholder: t('learners.dashboard.recommendations.search.placeholder'),
        useContentFilter: true,
      },
    },
    '/dashboard/workshops': {
      showHeader: true,
      pageTitle: t('learners.dashboard.workshops.title'),
      search: {
        show: false, // TODO: Enable this when we have a search for workshops
        placeholder: t('learners.dashboard.workshops.search.placeholder'),
        useContentFilter: false,
      },
    },
    '/dashboard/achievements': {
      showHeader: true,
      pageTitle: t('learners.dashboard.achievements.yourAchievements'),
      search: {
        show: false,
        placeholder: '',
        useContentFilter: false,
      },
    },
  };

  return (
    <Stack
      spacing={{ base: 0, lg: 8 }}
      direction={{ base: 'column', lg: 'row' }}
      paddingY={{ base: 0, md: 6 }}
      paddingLeft={{ base: 0, md: 6 }}
      height="calc(100vh - 50px)"
      overflow="hidden"
    >
      {/* Mobile Navigation - Sticky to top */}
      {isMobileOrTablet && (
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          backgroundColor="white"
          borderBottom="1px solid"
          borderColor="neutral.100"
          paddingTop={8}
          paddingBottom={0}
          flexShrink={0}
          marginBottom={8}
        >
          <LearnerDashboardMobileNavigation />
        </Box>
      )}

      {/* Desktop Side Navigation - Sticky to left */}
      {!isMobileOrTablet && (
        <Box
          position="sticky"
          top={0}
          left={0}
          width="254px"
          height="fit-content"
          border="1px solid"
          borderColor="neutral.100"
          borderRadius="12px"
          backgroundColor="white"
          overflowY="auto"
          flexShrink={0}
        >
          <LearnerDashboardSideNavigation />
        </Box>
      )}

      {/* Content Area */}
      <Box
        flex={1}
        width={{
          base: '100%', // Full width on mobile/tablet
          lg: 'calc(100% - 254px - 2rem)', // Viewport width minus side nav width and spacing (2rem = 8 from Stack spacing)
        }}
        display="flex"
        flexDirection="column"
        minHeight={0} // Important for flex child to shrink properly
      >
        {routeMap[pathname].showHeader && (
          <Stack
            paddingY={2}
            paddingX={{ base: 4, md: 0 }}
            backgroundColor="white"
            justifyContent="space-between"
            alignItems={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            width="full"
            flexShrink={0}
          >
            <Text fontSize="28px" fontWeight="600" color="baseBlack">
              {routeMap[pathname].pageTitle}
            </Text>

            {routeMap[pathname].search.show && (
              <Stack
                spacing={4}
                direction={{ base: 'column', md: 'row' }}
                width={{ base: 'full', md: 'auto' }}
                paddingRight={{ base: 0, md: 8 }}
              >
                <InputGroup>
                  <InputLeftElement>
                    <Search2 />
                  </InputLeftElement>

                  <Input
                    variant="create"
                    placeholder={`${t('learners.dashboard.recentlyViewed.search.placeholder')}`}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                <WarmNeutralMenu
                  label=""
                  options={filterOptions}
                  selected={selectedFilter}
                  setSelected={setSelectedFilter}
                  minWidth={175}
                />

                {pathname === '/dashboard/recently-viewed' && (
                  <DateFilterMenu
                    label={t('learners.dashboard.recentlyViewed.dateFilter.label')}
                    options={dateFilterOptions}
                    selected={selectedDateFilter}
                    setSelected={setSelectedDateFilter}
                    minWidth={175}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}

        <Box
          ref={contentContainerRef}
          overflowY="auto"
          flex={1}
          minHeight={0} // Important for flex child to shrink properly
          data-content-container="true"
          paddingX={{ base: 4, md: 0 }}
          paddingRight={{ base: 4, md: 8 }}
          paddingBottom={24}
        >
          <Outlet context={{ searchTerm, selectedFilter, selectedDateFilter }} />
        </Box>
      </Box>
    </Stack>
  );
}
